import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../Redux/Actions/UserAction';

/* [CF 2026-08-09 #16] The navigation drawer, extracted out of Header.js so the
   HOME page can render it too (client: "I need that menu to appear also on the
   home page"). Home has no <Header/> — it is a full-screen Swiper with its own
   hamburger — so the drawer had to become a component both surfaces can mount.
   It reads what it needs from redux rather than taking props, which keeps both
   call sites to `<NavDrawer open onClose />`.

   NOTE its CSS lives in style.css, NOT custom.css: Home.js deletes every
   custom.css <link> on mount, so drawer rules kept there would be missing on
   exactly the page we just added it to. */

const BUSINESS_KEYS = {
  CAR01: 'sp-spare-parts-business',
  CAR02: 'sp-garages-business',
  CAR03: 'sp-car-dealers-business',
  CAR04: 'sp-rent-car-business',
  CAR06: 'sp-car-wash-business',
  CAR07: 'sp-car-insurance-business',
  CAR08: 'sp-driving-school-business',
  CAR09: 'sp-car-protection-business',
  CAR10: 'sp-car-towing',
  CAR11: 'sp-taxi-business',
  CAR12: 'sp-air-conditioning-business',
  CAR13: 'sp-quick-services-business',
  CAR14: 'carAgentId',
};

/* One source of truth for the category list — adding or renaming one is a
   single line. `key` uses the client's own menu wording where they gave it
   ("Car Rentals", "Car Wash & Detailing", "Protection & Tinting"); those keys
   are menu-scoped, so the page titles elsewhere are untouched. */
export const NAV_CATEGORIES = [
  { to: '/user-cars-for-sale', key: 'Cars for Sale', icon: 'car-for-sale.svg' },
  { to: '/user-car-dealers', key: 'Car Dealers', icon: 'car-dealer.svg' },
  { to: '/user-rent-car', key: 'Car Rentals', icon: 'rent-a-car.svg' },
  { to: '/user-car-wash', key: 'Car Wash & Detailing', icon: 'car-wash.svg' },
  { to: '/user-car-protection', key: 'Protection & Tinting', icon: 'car-protection.svg' },
  { to: '/user-spare-parts', key: 'Spare Parts', icon: 'spare-parts.svg' },
  { to: '/user-quick-services', key: 'Quick Services', icon: 'quick-services.svg' },
  { to: '/user-garages', key: 'Garages', icon: 'garage.svg' },
  { to: '/user-driving-schools', key: 'Driving School', icon: 'driving-school.svg' },
  { to: '/user-car-insurance', key: 'Car Insurance', icon: 'car-insurance.svg' },
  { to: '/user-air-conditioning', key: 'Air Conditioning', icon: 'air-conditioning.svg' },
  { to: '/user-taxi', key: 'Taxi', icon: 'taxi.svg' },
  { to: '/user-towing', key: 'Car Towing', icon: 'car-towing.svg' },
];

export default function NavDrawer({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const getMybusiness = useSelector((state) => state?.home?.getMybusiness);

  const loggedIn = sessionStorage.getItem('loged_In') === 'true';
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';

  const toggleLanguage = () => {
    const next = currentLang === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
    localStorage.setItem('lang', next);
    document.documentElement.dir = next === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = next;
  };

  const onLogout = (e) => {
    if (e) e.preventDefault();
    dispatch(logout());
    onClose();
  };

  return (
    <>
      <div className={`mobile-sidebar cav-nav ${open ? 'open' : ''}`}>
        <div className="cav-nav-top">
          <Link to="/" className="cav-nav-brand" onClick={onClose}>
            <img src="assets/images/car-avenue-logo-light.svg" alt="Car Avenue" />
          </Link>
          <button className="cav-nav-close" aria-label={t('Close')} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="cav-nav-scroll">
          {!loggedIn ? (
            <div className="cav-nav-auth">
              <Link to="/login" className="cav-nav-btn cav-nav-btn--primary" onClick={onClose}>
                {t('login')}
              </Link>
              <Link to="/register" className="cav-nav-btn cav-nav-btn--ghost" onClick={onClose}>
                {t('Register')}
              </Link>
            </div>
          ) : (
            <>
              <Link to="/settings-edit-profile" className="cav-nav-account" onClick={onClose}>
                <span className="cav-nav-avatar">
                  <i className="fas fa-user" />
                </span>
                <span className="cav-nav-account-text">
                  <strong>{t('My Account')}</strong>
                  <small>{t('Edit Profile')}</small>
                </span>
                <i className="fas fa-chevron-right cav-nav-chev" />
              </Link>

              <ul className="cav-nav-list">
                <li>
                  <Link to="/settings-my-posts" onClick={onClose}>
                    <i className="far fa-newspaper cav-nav-ico" />
                    {t('My Posts')}
                  </Link>
                </li>
                <li>
                  <Link to="/settings-saved-items" onClick={onClose}>
                    <i className="far fa-heart cav-nav-ico" />
                    {t('Saved Items')}
                  </Link>
                </li>
                {getMybusiness?.businessId ? (
                  <li>
                    <Link
                      to={`/${BUSINESS_KEYS[getMybusiness?.businessCode] || 'sp-dashboard'}`}
                      onClick={onClose}
                    >
                      <i className="far fa-building cav-nav-ico" />
                      {t('Manage Business')}
                    </Link>
                  </li>
                ) : (
                  <li>
                    <Link to="/settings-become-a-seller" onClick={onClose}>
                      <i className="fas fa-store cav-nav-ico" />
                      {t('Become a Seller')}
                    </Link>
                  </li>
                )}
              </ul>
            </>
          )}

          <Link
            to={loggedIn ? '/sp-cars-for-sale' : '/login'}
            className="cav-nav-cta"
            onClick={onClose}
          >
            <i className="fas fa-plus" />
            {t('Add Car for Sale')}
          </Link>

          <p className="cav-nav-heading">{t('Website Categories')}</p>
          <ul className="cav-nav-list cav-nav-list--cats">
            {NAV_CATEGORIES.map((c) => (
              <li key={c.to}>
                <Link to={c.to} onClick={onClose}>
                  <img
                    className="cav-nav-cat-ico"
                    src={`assets/images/service-icons/${c.icon}`}
                    alt=""
                    aria-hidden="true"
                  />
                  {t(c.key)}
                  <i className="fas fa-chevron-right cav-nav-chev" />
                </Link>
              </li>
            ))}
          </ul>

          <div className="cav-nav-foot">
            <button type="button" className="cav-nav-lang" onClick={toggleLanguage}>
              <i className="fas fa-globe" />
              {currentLang === 'ar' ? 'English' : 'العربية'}
            </button>
            {loggedIn && (
              <button type="button" className="cav-nav-logout" onClick={onLogout}>
                <i className="fas fa-sign-out-alt" />
                {t('Logout')}
              </button>
            )}
          </div>
        </div>
      </div>

      {open && <div className="mobile-overlay" onClick={onClose}></div>}
    </>
  );
}
